import { resourcesApi } from '../services/resources-api';

interface Stats {
  total: number;
  byType: Record<string, number>;
  byCategory: Record<string, number>;
  pendingReview: number;
}

export class StatsDashboardView {
  private container: HTMLElement;
  private stats: Stats | null = null;
  private isLoading = false;

  constructor() {
    const el = document.getElementById('stats-view');
    if (!el) throw new Error('stats-view element not found');
    this.container = el;
  }

  async load(): Promise<void> {
    if (this.isLoading) return;
    this.isLoading = true;
    this.renderLoading();

    try {
      const result = await resourcesApi.stats();
      this.stats = result.data;
      this.render();
    } catch (error) {
      this.renderError('Failed to load statistics');
    } finally {
      this.isLoading = false;
    }
  }

  private render(): void {
    if (!this.stats) return;

    const s = this.stats;

    this.container.innerHTML = `
      <div class="stats-dashboard">
        <div class="stats-header">
          <h2>Statistics</h2>
          <button class="btn-icon" id="btn-refresh" title="Refresh">
            <span class="material-symbols-outlined">refresh</span>
          </button>
        </div>

        <div class="stats-cards">
          ${this.renderCard('Total', s.total, 'inventory_2', 'primary')}
          ${this.renderCard('Pending', s.pendingReview, 'rate_review', 'warning')}
          ${this.renderCard('Knowledge', s.byType['knowledge'] || 0, 'hub', 'info')}
          ${this.renderCard('Multimedia', s.byType['multimedia'] || 0, 'movie', 'success')}
        </div>

        <div class="stats-sections">
          <div class="stats-section">
            <h3>By Type</h3>
            <div class="stats-bars">
              ${this.renderBars(s.byType)}
            </div>
          </div>

          <div class="stats-section">
            <h3>By Category</h3>
            <div class="stats-bars">
              ${this.renderBars(s.byCategory)}
            </div>
          </div>
        </div>
      </div>
    `;

    this.bindEvents();
  }

  private bindEvents(): void {
    this.container.querySelector('#btn-refresh')?.addEventListener('click', () => this.load());

    this.container.querySelectorAll('.stats-bar-row').forEach(row => {
      row.addEventListener('click', () => {
        const filterType = (row as HTMLElement).dataset.filterType;
        const filterValue = (row as HTMLElement).dataset.filterValue;
        if (filterType && filterValue) {
          window.dispatchEvent(new CustomEvent('navigate-filter', {
            detail: { view: 'knowledge', [filterType]: filterValue }
          }));
        }
      });
    });
  }

  private renderCard(label: string, value: number, icon: string, color: string): string {
    return `
      <div class="stats-card stats-card-${color}">
        <span class="material-symbols-outlined stats-card-icon">${icon}</span>
        <div class="stats-card-value">${this.formatNumber(value)}</div>
        <div class="stats-card-label">${label}</div>
      </div>
    `;
  }

  private renderBars(data: Record<string, number>): string {
    const entries = Object.entries(data)
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);

    if (entries.length === 0) {
      return '<p class="stats-empty">No data</p>';
    }

    const max = Math.max(...entries.map(([, v]) => v), 1);
    const isType = entries.some(([k]) => ['notes', 'knowledge', 'multimedia'].includes(k));

    return entries.map(([key, value]) => `
      <div class="stats-bar-row" data-filter-type="${isType ? 'type' : 'category'}" data-filter-value="${key}">
        <span class="stats-bar-label">${this.formatLabel(key)}</span>
        <div class="stats-bar-track">
          <div class="stats-bar-fill" style="width: ${(value / max) * 100}%"></div>
        </div>
        <span class="stats-bar-value">${value}</span>
      </div>
    `).join('');
  }

  private formatLabel(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  private formatNumber(num: number): string {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  }

  private renderLoading(): void {
    this.container.innerHTML = `
      <div class="stats-loading">
        <span class="material-symbols-outlined spinning">progress_activity</span>
        <p>Loading statistics...</p>
      </div>
    `;
  }

  private renderError(message: string): void {
    this.container.innerHTML = `
      <div class="stats-error">
        <span class="material-symbols-outlined">error</span>
        <h3>Error</h3>
        <p>${message}</p>
        <button class="btn btn-primary" id="btn-retry">Retry</button>
      </div>
    `;
    this.container.querySelector('#btn-retry')?.addEventListener('click', () => this.load());
  }
}
